import React, { FC, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import GoatMascot, { GoatMascotVariant } from './GoatMascot';

interface EmptyStateProps {
  /** Contextual headline, e.g. "No posts yet". */
  title: string;
  /** Optional supporting line, e.g. "Be the first to post something." */
  subtitle?: string;
  /** Mascot personality — bob for empties, confused for errors. */
  variant?: GoatMascotVariant;
  /** Mascot size in px. */
  size?: number;
  /** Optional call-to-action rendered under the text. */
  action?: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Shared empty-screen treatment: the animated goat mascot with a contextual
 * message. Used for empty feeds/lists, no-results and error surfaces so every
 * "nothing here" moment looks intentional and on-brand.
 */
const EmptyState: FC<EmptyStateProps> = ({
  title,
  subtitle,
  variant = 'bob',
  size = 72,
  action,
  sx,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: 1,
      py: 4,
      px: 2,
      ...sx,
    }}
  >
    <GoatMascot variant={variant} size={size} />
    <Typography
      sx={{
        mt: 1,
        fontFamily: (t) => t.tokens.typography.displayFontFamily,
        fontSize: '17px',
        color: 'text.primary',
      }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
        {subtitle}
      </Typography>
    )}
    {action && <Box sx={{ mt: 1 }}>{action}</Box>}
  </Box>
);

export default EmptyState;
